import React, {createContext, useContext, useEffect, useState} from 'react'
import {ThemeModeComponent} from '../../../assets/ts/layout/ThemeMode'
import {toAbsoluteUrl} from '../../../helpers'

export type ThemeModeType = 'dark' | 'light' | 'system'
export const themeModelSKey = 'kt_theme_mode_value'
export const themeMenuModeLSKey = 'kt_theme_mode_menu'

let systemMode: 'light' | 'dark' = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'

type ThemeModeContextType = {
  mode: ThemeModeType
  menuMode: ThemeModeType
  updateMode: (_mode: ThemeModeType) => void
  updateMenuMode: (_mode: ThemeModeType) => void
}

const themeModeSwitchHelper = (_mode: ThemeModeType) => {
  // change background image url
  const mode = _mode !== 'system' ? _mode : ThemeModeComponent.getSystemMode()
  const imageUrl = '/media/patterns/header-bg' + (mode === 'light' ? '.jpg' : '-dark.png')
  document.body.style.backgroundImage = `url("${toAbsoluteUrl(imageUrl)}")`
}

const defaultThemeMode: ThemeModeContextType = {
  mode: 'system',
  menuMode: 'system',
  updateMode: (_mode: ThemeModeType) => {},
  updateMenuMode: (_menuMode: ThemeModeType) => {},
}

const ThemeModeContext = createContext<ThemeModeContextType>({
  mode: defaultThemeMode.mode,
  menuMode: defaultThemeMode.menuMode,
  updateMode: (_mode: ThemeModeType) => {},
  updateMenuMode: (_menuMode: ThemeModeType) => {},
})

const useThemeMode = () => useContext(ThemeModeContext)

const ThemeModeProvider = ({children}: {children: React.ReactNode}) => {
  const [mode, setMode] = useState<ThemeModeType>(() => ThemeModeComponent.getMode())
  const [menuMode, setMenuMode] = useState<ThemeModeType>(
    () => ThemeModeComponent.getMenuMode() || 'system'
  )

  const updateMode = (_mode: ThemeModeType, saveInLocalStorage: boolean = true) => {
    const resolvedMode = _mode === 'system' ? ThemeModeComponent.getSystemMode() : _mode
    setMode(resolvedMode)
    setMenuMode(_mode)
    if (saveInLocalStorage) {
      ThemeModeComponent.setMode(resolvedMode, _mode)
    }
  }

  const updateMenuMode = (_menuMode: ThemeModeType, saveInLocalStorage: boolean = true) => {
    updateMode(_menuMode, saveInLocalStorage)
  }

  useEffect(() => {
    const media = window.matchMedia('(prefers-color-scheme: dark)')
    const element = document.documentElement
    const syncMode = () => {
      systemMode = ThemeModeComponent.getSystemMode()
      setMode(ThemeModeComponent.getMode())
      setMenuMode(ThemeModeComponent.getMenuMode() || 'system')
    }
    const handleSystemChange = () => {
      systemMode = ThemeModeComponent.getSystemMode()
      if (ThemeModeComponent.getMenuMode() === 'system') {
        ThemeModeComponent.setMode(ThemeModeComponent.getSystemMode(), 'system')
      }
    }
    element.addEventListener('kt.thememode.change', syncMode)
    media.addEventListener('change', handleSystemChange)
    ThemeModeComponent.init()
    return () => {
      element.removeEventListener('kt.thememode.change', syncMode)
      media.removeEventListener('change', handleSystemChange)
    }
  }, [])

  return (
    <ThemeModeContext.Provider value={{mode, menuMode, updateMode, updateMenuMode}}>
      {children}
    </ThemeModeContext.Provider>
  )
}

export {ThemeModeProvider, useThemeMode, systemMode, themeModeSwitchHelper}
